import { useCallback, useEffect, useRef, useState } from 'react'
import { AppState, Platform } from 'react-native'
import { createNavigationContainerRef, createStaticNavigation } from '@react-navigation/native'
import { createNativeStackNavigator } from '@react-navigation/native-stack'
import { PairingScreen, PairingInputScreen, PairingSuccessScreen } from '../features/cashier/pairing'
import { CashierHomeScreen } from '../features/cashier/home'
import { AdminShell } from '../features/admin/AdminShell'
import {
  AdminCheckEmailScreen,
  AdminCreatePasswordScreen,
  AdminForgotPasswordScreen,
  AdminResetLinkErrorScreen,
  AdminSignInScreen,
} from '../features/admin/auth'
import { isActiveAdminAccount } from '../features/admin/auth'
import {
  clearAdminAuthUrl,
  getAdminPasswordRecoveryTokenHash,
  hasAdminPasswordRecoveryUrl,
  hasAdminPasswordResetUrlError,
  hasAdminUrlHash,
  hasUnknownAdminAuthPath,
} from '../features/admin/auth/authUrl'
import { supabase } from '../lib/supabase'
import { reportUnexpectedError } from '../lib/reportError'
import type { User } from '@supabase/supabase-js'
import type { AdminAuthStackParamList, CashierStackParamList } from './types'

const cashierScreens = {
  Pairing: PairingScreen,
  PairingInput: PairingInputScreen,
  PairingSuccess: PairingSuccessScreen,
  Home: CashierHomeScreen,
}

// Cashier (iOS/Android) — static API, same pattern as customer app.
// The screen order determines the first route, so use two stacks to avoid
// mounting Pairing before an already-paired device routes to Home.
const CashierPairingStack = createNativeStackNavigator({
  screenOptions: { headerShown: false },
  screens: cashierScreens,
})

const CashierHomeStack = createNativeStackNavigator({
  screenOptions: { headerShown: false },
  screens: {
    Home: CashierHomeScreen,
    Pairing: PairingScreen,
    PairingInput: PairingInputScreen,
    PairingSuccess: PairingSuccessScreen,
  },
})

const CashierPairingNavigation = createStaticNavigation(CashierPairingStack)
const CashierHomeNavigation = createStaticNavigation(CashierHomeStack)
export const cashierNavigationRef = createNavigationContainerRef<CashierStackParamList>()

function isCashierDeviceUser(user: User | null) {
  return user?.app_metadata?.account_type === 'cashier_device'
}

async function isActiveCashierDeviceSession() {
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error || !user || !isCashierDeviceUser(user)) return false

  const deviceId = user.app_metadata?.device_id
  if (typeof deviceId !== 'string') return false

  const { data: device, error: deviceError } = await supabase
    .from('devices')
    .select('id')
    .eq('id', deviceId)
    .eq('status', 'active')
    .maybeSingle()

  return !deviceError && !!device
}

interface CashierNavigationWithSessionGateProps {
  onSessionReady?: () => void
}

type CashierInitialRoute = 'Home' | 'Pairing'

export function CashierNavigationWithSessionGate({ onSessionReady }: CashierNavigationWithSessionGateProps) {
  const [initialRoute, setInitialRoute] = useState<CashierInitialRoute | null>(null)

  const routeBySession = useCallback(async () => {
    const isActive = await isActiveCashierDeviceSession()
    if (!isActive) {
      await supabase.auth.signOut()
      return 'Pairing'
    }
    return 'Home'
  }, [])

  useEffect(() => {
    let isMounted = true
    routeBySession()
      .then((route) => {
        if (isMounted) {
          setInitialRoute(route)
          onSessionReady?.()
        }
      })
      .catch(async () => {
        await supabase.auth.signOut()
        if (isMounted) {
          setInitialRoute('Pairing')
          onSessionReady?.()
        }
      })
    return () => {
      isMounted = false
    }
  }, [onSessionReady, routeBySession])

  useEffect(() => {
    if (!initialRoute) return
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        void routeBySession().then((route) => {
          cashierNavigationRef.resetRoot({ index: 0, routes: [{ name: route }] })
          setInitialRoute(route)
        })
      }
    })
    return () => subscription.remove()
  }, [initialRoute, routeBySession])

  if (!initialRoute) return null

  const Navigation = initialRoute === 'Home' ? CashierHomeNavigation : CashierPairingNavigation
  return <Navigation ref={cashierNavigationRef} />
}

const AdminAuthStack = createNativeStackNavigator({
  screenOptions: { headerShown: false },
  screens: {
    SignIn: AdminSignInScreen,
    ForgotPassword: AdminForgotPasswordScreen,
    CheckEmail: AdminCheckEmailScreen,
    CreatePassword: AdminCreatePasswordScreen,
    ResetLinkError: AdminResetLinkErrorScreen,
    AdminHome: () => <AdminShell initialRouteName="AdminHome" />,
    AdminMembers: () => <AdminShell initialRouteName="AdminMembers" />,
    AdminRecentActivity: () => <AdminShell initialRouteName="AdminRecentActivity" />,
    AdminAuditLog: () => <AdminShell initialRouteName="AdminAuditLog" />,
    AdminLoyalty: () => <AdminShell initialRouteName="AdminLoyalty" />,
    AdminBirthdayReward: () => <AdminShell initialRouteName="AdminBirthdayReward" />,
    AdminCashiersDevices: () => <AdminShell initialRouteName="AdminCashiersDevices" />,
    AdminSettings: () => <AdminShell initialRouteName="AdminSettings" />,
  },
})

const AdminAuthNavigation = createStaticNavigation(AdminAuthStack)
const adminAuthNavigationRef = createNavigationContainerRef<AdminAuthStackParamList>()

export function AdminNavigation() {
  const [isAuthReady, setIsAuthReady] = useState(false)
  const hasHandledPasswordRecoveryRef = useRef(false)

  const openCreatePasswordFromRecovery = useCallback(() => {
    if (hasHandledPasswordRecoveryRef.current) return
    hasHandledPasswordRecoveryRef.current = true
    clearAdminAuthUrl()
    adminAuthNavigationRef.navigate('CreatePassword', { isRecovery: true })
  }, [])

  // onReady fires once the navigation container has initialised its state and
  // is ready to accept navigation calls.
  const handleReady = useCallback(async () => {
    try {
      if (hasAdminPasswordResetUrlError()) {
        adminAuthNavigationRef.navigate('ResetLinkError')
        clearAdminAuthUrl()
        return
      }

      const recoveryTokenHash = getAdminPasswordRecoveryTokenHash()
      if (recoveryTokenHash) {
        try {
          const { error } = await supabase.auth.verifyOtp({ token_hash: recoveryTokenHash, type: 'recovery' })
          if (error) {
            // Expired/already-used link — a routine outcome, not a bug.
            adminAuthNavigationRef.navigate('ResetLinkError')
            clearAdminAuthUrl()
          }
          // On success, verifyOtp fires a PASSWORD_RECOVERY auth-state-change
          // event, which the listener below already handles.
        } catch (err) {
          reportUnexpectedError(err)
          adminAuthNavigationRef.navigate('ResetLinkError')
          clearAdminAuthUrl()
        }
        return
      }

      if (hasAdminPasswordRecoveryUrl()) {
        openCreatePasswordFromRecovery()
        return
      }

      if (hasAdminUrlHash() || hasUnknownAdminAuthPath()) {
        clearAdminAuthUrl()
      }

      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user && await isActiveAdminAccount(session.user.id)) {
        adminAuthNavigationRef.navigate('AdminHome')
      }
    } finally {
      setIsAuthReady(true)
    }
  }, [openCreatePasswordFromRecovery])

  useEffect(() => {
    if (!isAuthReady || Platform.OS !== 'web' || typeof document === 'undefined') return
    const splash = document.getElementById('web-splash')
    if (!splash) return
    splash.style.opacity = '0'
    const timer = setTimeout(() => splash.remove(), 300)
    return () => clearTimeout(timer)
  }, [isAuthReady])

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        openCreatePasswordFromRecovery()
      }
    })

    return () => subscription.unsubscribe()
  }, [openCreatePasswordFromRecovery])

  // Browser tabs throttle background timers, which can let the access token
  // expire before the auto-refresh interval fires. Proactively refresh when
  // the tab becomes visible so edge function calls always have a fresh token.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        void supabase.auth.getSession()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  return <AdminAuthNavigation ref={adminAuthNavigationRef} onReady={handleReady} />
}
